import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import Filtro from '../components/Filtro';
import CardFaturamento from '../components/visao_geral/CardFaturamento';
import CardLucro from '../components/visao_geral/CardLucro';
import CardCusto from '../components/visao_geral/CardCusto';
import CardLitros from '../components/visao_geral/CardLitros';
import CardAbastecimento from '../components/visao_geral/CardAbastecimento';
import CardPostos from '../components/visao_geral/CardPostos';
import FormasPagamento from '../components/visao_geral/FormasPagamento';
import GraficoAlternado from '../components/visao_geral/GraficoAlternado';
import GraficoEvolucao from '../components/visao_geral/GraficoEvolucao';
import TabelaAnalise from '../components/visao_geral/TabelaAnalise';
import LitrosVendidosCombustivel from '../components/visao_geral/LitrosVendidosCombustivel';
import { useVisaoGeral } from '../hooks/useVisaoGeral';
import { construirFiltrosAPI } from '../utils/filtrosHelper';
import { calcularPeriodoComparacao } from '../utils/periodoComparacao';
import { prefetchVisaoGeralDashboard } from '../services/api';
import { serializarFiltros } from '../utils/compararFiltros';

const VisaoGeral = ({ showFilters = true, onDataUpdate }) => {
  const [filtrosSelecionados, setFiltrosSelecionados] = useState({});

  // Estado para MÚLTIPLOS filtros cruzados (cross-filter)
  // Formato: { posto: { valor, origem }, produto: { valor, origem }, ... }
  const [filtrosCruzados, setFiltrosCruzados] = useState({});

  // Combinar filtros normais com filtros cruzados
  const filtrosCombinados = useMemo(() => {
    const novosFiltros = { ...filtrosSelecionados };

    // Aplicar todos os filtros cruzados ativos
    Object.entries(filtrosCruzados).forEach(([tipo, dados]) => {
      if (dados?.valor) {
        switch (tipo) {
          case 'posto':
            novosFiltros.posto = [dados.valor];
            break;
          case 'produto':
            novosFiltros.produto = [dados.valor];
            break;
          case 'funcionario':
            novosFiltros.funcionario = [dados.valor];
            break;
          case 'cidade':
            novosFiltros.cidade = [dados.valor];
            break;
          case 'turno':
            novosFiltros.turno = [dados.valor];
            break;
          case 'forma_pagamento':
            novosFiltros.forma_pagamento = [dados.valor];
            break;
          case 'estado':
            novosFiltros.estado = [dados.valor];
            break;
          case 'data':
            // Para filtro de data, usar o formato correto para a API
            novosFiltros['visao_geral.data_hora_abastecimento_sp_date'] = dados.valor;
            break;
          default:
            break;
        }
      }
    });

    return novosFiltros;
  }, [filtrosSelecionados, filtrosCruzados]);

  const { dados, loading, error } = useVisaoGeral(filtrosCombinados);

  // ===== Prefetch (Visão Geral): 1 request por página =====
  const filtrosCombinadosKey = useMemo(() => serializarFiltros(filtrosCombinados), [filtrosCombinados]);
  const filtrosApiAtual = useMemo(() => construirFiltrosAPI(filtrosCombinados), [filtrosCombinadosKey]);

  const filtrosComparacao = useMemo(() => {
    if (filtrosCombinados?.dataMin && filtrosCombinados?.dataMax) {
      const pc = calcularPeriodoComparacao(
        filtrosCombinados.dataMin,
        filtrosCombinados.dataMax,
        filtrosCombinados.periodo || 'customizado'
      );
      if (!pc) return null;
      return {
        ...filtrosCombinados,
        periodo: 'customizado',
        dataMin: pc.dataMin,
        dataMax: pc.dataMax,
      };
    }

    // Fallback: ontem (mantém comportamento do hook)
    const hoje = new Date();
    const formatarData = (data) => {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const dia = String(data.getDate()).padStart(2, '0');
      return `${ano}-${mes}-${dia}`;
    };
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    return {
      ...filtrosCombinados,
      periodo: 'customizado',
      dataMin: formatarData(ontem),
      dataMax: formatarData(ontem),
    };
  }, [filtrosCombinadosKey]);

  const filtrosApiComparacao = useMemo(() => {
    return filtrosComparacao ? construirFiltrosAPI(filtrosComparacao) : null;
  }, [filtrosComparacao]);

  const filtrosApiAtualKey = useMemo(() => serializarFiltros(filtrosApiAtual), [filtrosApiAtual]);
  const filtrosApiComparacaoKey = useMemo(() => serializarFiltros(filtrosApiComparacao || {}), [filtrosApiComparacao]);

  const prefetchTimeoutRef = useRef(null);
  const prefetchPromiseRef = useRef(null);

  useLayoutEffect(() => {
    // Só faz prefetch quando já existe filtro de data (evita bursts durante init do filtro)
    if (!filtrosApiAtual?.['visao_geral.data_hora_abastecimento_sp_date']) return;

    // Debounce curto: evita disparar várias vezes durante a inicialização/ajustes de filtros
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
    }

    // Prefetch em 2 estágios:
    // - 1) crítico: só o necessário para render rápido (roda imediatamente)
    // - 2) background: combos/abas (mapa/tabela/evolução) e seção de baixo
    // Stage 1 (crítico): KPIs + 129/135/141/136 básicos (para hook encontrar tudo no cache)
    // Aguardar o prefetch completar para garantir que cache está populado
    prefetchPromiseRef.current = prefetchVisaoGeralDashboard({
      filtersCurrent: filtrosApiAtual,
      filtersComparison: filtrosApiComparacao,
      prefetchAllInteractiveCombos: false,
      includeVisaoGeralBundle: false,
      includeKpis: true, // Incluir KPIs para evitar chamadas do hook
    }).catch(() => { });

    // Stage 2 (background): combos para evitar GETs ao trocar abas/indicadores
    prefetchTimeoutRef.current = setTimeout(() => {
      prefetchVisaoGeralDashboard({
        filtersCurrent: filtrosApiAtual,
        prefetchAllInteractiveCombos: true,
        includeVisaoGeralBundle: false,
      }).catch(() => { });
    }, 600);

    return () => {
      if (prefetchTimeoutRef.current) clearTimeout(prefetchTimeoutRef.current);
    };
  }, [filtrosApiAtualKey, filtrosApiComparacaoKey]);

  // Atualizar timestamp quando dados são carregados
  useEffect(() => {
    if (!loading && dados && onDataUpdate) {
      onDataUpdate();
    }
  }, [loading, dados, onDataUpdate]);

  // Callback para aplicar/remover filtro cruzado
  const aplicarFiltroCruzado = useCallback((tipo, valor, origem) => {
    setFiltrosCruzados(prev => {
      // Se clicar no mesmo item, remove apenas esse filtro
      if (prev[tipo]?.valor === valor) {
        const { [tipo]: removed, ...rest } = prev;
        return rest;
      }
      // Senão, adiciona/atualiza o filtro desse tipo
      return {
        ...prev,
        [tipo]: { valor, origem }
      };
    });
  }, []);

  // Limpar um filtro específico
  const limparFiltro = useCallback((tipo) => {
    setFiltrosCruzados(prev => {
      const { [tipo]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Limpar todos os filtros cruzados
  const limparTodosFiltros = useCallback(() => {
    setFiltrosCruzados({});
  }, []);

  // Verificar quantos filtros estão ativos
  const filtrosAtivos = Object.entries(filtrosCruzados).filter(([_, dados]) => dados?.valor);
  const temFiltrosAtivos = filtrosAtivos.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Área de filtros - com animação de mostrar/ocultar */}
      <div
        className={`w-full mx-auto transition-all duration-300 ease-in-out overflow-hidden ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ padding: '0 var(--space-fluid-md)' }}
      >
        <Filtro onChange={setFiltrosSelecionados} />
      </div>

      {/* Badge de Filtros Cruzados Ativos */}
      {temFiltrosAtivos && (
        <div className="w-full mx-auto" style={{ padding: '0 var(--space-fluid-sm)', marginTop: 'var(--space-fluid-sm)' }}>
          <div className="cross-filter-badge">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <span className="text-amber-600 font-medium" style={{ fontSize: 'var(--text-fluid-xs)' }}>
                🔗 Filtros cruzados ativos ({filtrosAtivos.length}):
              </span>
              <button
                onClick={limparTodosFiltros}
                className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg font-medium transition-colors"
                style={{ fontSize: 'var(--text-fluid-xs)', padding: 'var(--space-fluid-xs) var(--space-fluid-sm)' }}
              >
                ✕ Limpar todos
              </button>
            </div>
            <div className="flex flex-wrap" style={{ gap: 'var(--space-fluid-xs)' }}>
              {filtrosAtivos.map(([tipo, dados]) => (
                <div key={tipo} className="cross-filter-tag">
                  <span className="truncate" style={{ maxWidth: 'clamp(100px, 20vw, 200px)' }}>
                    {tipo === 'posto' && '📍 Posto: '}
                    {tipo === 'produto' && '🛢️ Produto: '}
                    {tipo === 'funcionario' && '👤 Funcionário: '}
                    {tipo === 'turno' && '🕐 Turno: '}
                    {tipo === 'forma_pagamento' && '💳 Pagamento: '}
                    {tipo === 'estado' && '🗺️ Estado: '}
                    {tipo === 'data' && '📅 Data: '}
                    {dados.valor}
                  </span>
                  <button
                    onClick={() => limparFiltro(tipo)}
                    className="text-amber-600 hover:text-amber-900 flex-shrink-0"
                    title={`Remover filtro de ${tipo}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cards de métricas - Grid auto-adaptável */}
      <div className="w-full mx-auto" style={{ padding: '0 var(--space-fluid-sm)', marginTop: 'var(--space-fluid-sm)' }}>
        <div className="metrics-grid">
          <CardFaturamento dados={dados.faturamento} loading={loading} textoComparacao={dados.textoComparacao} />
          <CardLucro dados={dados.lucro} loading={loading} textoComparacao={dados.textoComparacao} />
          <CardCusto dados={dados.custo} loading={loading} textoComparacao={dados.textoComparacao} />
          <CardLitros dados={dados.litros} loading={loading} textoComparacao={dados.textoComparacao} />
          <CardAbastecimento dados={dados.abastecimento} loading={loading} textoComparacao={dados.textoComparacao} />
          <CardPostos dados={dados.postos} loading={loading} />
        </div>

        {/* Gráfico de Evolução e Abastecimentos por Turno lado a lado */}
        <div className="charts-grid" style={{ marginTop: 'var(--space-fluid-md)' }}>
          <div className="w-full h-full chart-container overflow-hidden min-h-[400px] max-h-[400px]">
            <GraficoEvolucao
              filtros={filtrosApiAtual}
              onCrossFilter={(tipo, valor) => aplicarFiltroCruzado(tipo, valor, 'Gráfico de Evolução')}
              filtroCruzadoAtivo={filtrosCruzados.data || null}
            />
          </div>
          <div className="w-full h-full chart-container overflow-hidden min-h-[400px] max-h-[400px]">
            <GraficoAlternado
              dadosTurno={dados.abastecimentosTurno}
              filtros={filtrosCombinados}
              loading={loading}
              onCrossFilter={(tipo, valor) => aplicarFiltroCruzado(tipo, valor, tipo === 'turno' ? 'Gráfico de Turnos' : 'Mapa de Postos')}
              filtroCruzadoAtivo={filtrosCruzados}
            />
          </div>
        </div>

        {/* Seção de Formas de Pagamento, Litros por Combustível e Tabela de Análise */}
        <div className="bottom-grid" style={{ marginTop: 'var(--space-fluid-md)' }}>
          <div className="w-full h-full chart-container-bottom">
            <FormasPagamento
              dados={dados.formasPagamento}
              loading={loading}
              onCrossFilter={(valor) => aplicarFiltroCruzado('forma_pagamento', valor, 'Formas de Pagamento')}
              filtroCruzadoAtivo={filtrosCruzados.forma_pagamento?.valor || null}
            />
          </div>
          <div className="w-full h-full chart-container-bottom">
            <LitrosVendidosCombustivel
              filtros={filtrosApiAtual}
              loading={loading}
              onCrossFilter={(valor) => aplicarFiltroCruzado('produto', valor, 'Litros por Combustível')}
              filtroCruzadoAtivo={filtrosCruzados.produto?.valor || null}
            />
          </div>
          <div className="w-full h-full chart-container-bottom">
            <TabelaAnalise
              filtros={filtrosApiAtual}
              onCrossFilter={(tipo, valor) => aplicarFiltroCruzado(tipo, valor, 'Tabela de Análise')}
              filtroCruzadoAtivo={filtrosCruzados}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg" style={{ marginTop: 'var(--space-fluid-md)', padding: 'var(--space-fluid-sm)' }}>
            <p className="text-red-600" style={{ fontSize: 'var(--text-fluid-xs)' }}>
              Erro ao carregar dados. Tente novamente mais tarde.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisaoGeral;